import {
    map,
    mapTo,
    filter,
    startWith,
    tap,
    switchMap,
    takeUntil,
} from 'rxjs/operators';
import { Subject, from, merge } from 'rxjs';
import once from 'ramda/src/once';

import { RETRY_COUNT_CDN_ROTATION }                     from '../../Errors/Constants/Error.retries';
import ErrorType                                        from '../../Errors/Constants/Error.type';
import memoizeConnection                                from '../../Shared/Utils/memoizeConnection';
import { isStartupError }                               from '../Utils/cdnErrorFilters';
import { mapResumeFromToCurrentTime, extractLoadData }  from '../Utils/extractLoadData';
import manifestsGenerator                               from '../Utils/manifestsGenerator';
import createExcessiveBufferingStream                   from './createExcessiveBufferingStream';
import createRotationErrorsStream                       from './createRotationErrorsStream';

/**
 * @param  {playback-web-player.PlayerFacade} player
 * @param  {ErrorsHandler.handleError} handleError
 * @param  {[Playback, ResumePoints, Config, Asset]} loadData
 * @param  {Number} maxRetries
 */

const createManifestRotationStream = (
    player,
    handleError,
    loadData,
    maxRetries = RETRY_COUNT_CDN_ROTATION,
) => {
    const [{ manifests }] = loadData;
    const errorNotifier$ = new Subject();
    const createManifestGenerator = () => manifestsGenerator(manifests);
    let manifestGen = createManifestGenerator();
    let retries = 0;
    let errorType = null;

    const getErrorType = () => errorType;
    const setErrorType = error => {
        errorType = isStartupError(error)
            ? ErrorType.PLAYBACK_SOURCE_STARTUP
            : ErrorType.PLAYBACK_SOURCE_MIDSTREAM;
    };

    const mapToNextManifest = () => {
        let nextManifest = manifestGen.next().value;
        if (!nextManifest && (retries <= maxRetries)) {
            retries++;
            manifestGen = createManifestGenerator();
            nextManifest = manifestGen.next().value;
        }
        if (retries > maxRetries) {
            handleError({
                type: getErrorType(),
                source: loadData,
            });
            errorNotifier$.next();
            return void 0;
        }

        return nextManifest;
    };

    const rotationErrors$ = createRotationErrorsStream(player.errors$, errorNotifier$);
    const excessiveBuffering$ = createExcessiveBufferingStream(player.state.stores.playback.update$);

    const getIsConnectionOk = memoizeConnection();

    return merge(
        rotationErrors$,
        excessiveBuffering$,
    )
        .pipe(
            takeUntil(errorNotifier$),
            switchMap(error =>
                from(getIsConnectionOk())
                    .pipe(
                        filter(Boolean),
                        mapTo(error)
                    )
            ),
            tap(once(setErrorType)),
            map(mapToNextManifest),
            filter(Boolean),
            map(extractLoadData.bind(null, loadData)),
            map(mapResumeFromToCurrentTime.bind(null, getErrorType, player)),
            startWith(extractLoadData(loadData, manifestGen.next().value)),
        );
};

export default createManifestRotationStream;
